import { z } from 'zod'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { adAccountUseSchema } from '../enums/ad-account-use.enum.js'
import { leadStatsViewSchema } from '../enums/lead-stats-view.enum.js'
import { sourceRowDtoSchema } from './source-numbers.dto.js'

/**
 * Ad numbers as the Ads pages show them (docs/ad-analytics.spec.md, "The
 * numbers" and "Two-week periods"): totals per currency, never mixed, and every
 * rate worked out from them on the page (`lib/src/helpers/ad-numbers.ts`).
 */

/** One currency's totals for a stretch of days. Spend in micros, as Google reports it. */
export const adTotalsDtoSchema = z.object({
  currencyCode: z.string(),
  spendMicros: z.number().int().nonnegative(),
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  conversions: z.number().nonnegative(),
})
export type AdTotalsDto = z.infer<typeof adTotalsDtoSchema>

/** A row's numbers in a period, and in the stretch of the period before it is compared with. */
export const adComparedDtoSchema = z.object({
  current: z.array(adTotalsDtoSchema),
  earlier: z.array(adTotalsDtoSchema),
})
export type AdComparedDto = z.infer<typeof adComparedDtoSchema>

/** A period, what it counts, and what it is compared with. See `adPeriodWindow`. */
export const adPeriodWindowDtoSchema = z.object({
  start: calendarDaySchema,
  end: calendarDaySchema,
  /** The last day counted: the period's end, or the cut-off while it is in progress. */
  through: calendarDaySchema,
  days: z.number().int().min(1).max(14),
  inProgress: z.boolean(),
  compareStart: calendarDaySchema,
  compareEnd: calendarDaySchema,
})
export type AdPeriodWindowDto = z.infer<typeof adPeriodWindowDtoSchema>

/** An account whose numbers count toward the view, and whether it is keeping them current. */
export const adContributorDtoSchema = z.object({
  accountId: z.string().uuid(),
  name: z.string(),
  externalId: z.string(),
  use: adAccountUseSchema,
  /** Every campaign that follows it counts here (it is the venue's default). */
  isDefault: z.boolean(),
  /** When it isn't the default: the names of its campaigns that count here. */
  campaignNames: z.array(z.string()),
  /**
   * Why it isn't updating, in the spec's words, or null while it is: `stopped`,
   * `closed` (in Google), `unreachable`, `signin` (every sign-in reaching it is
   * refused), `no_connection`, `venue_deleted`, `importing_history`.
   */
  notUpdating: z
    .enum(['stopped', 'closed', 'unreachable', 'signin', 'no_connection', 'venue_deleted', 'importing_history'])
    .nullable(),
  /** The last of its own days whose numbers are in. */
  importedThrough: calendarDaySchema.nullable(),
  lastUpdatedAt: instantSchema.nullable(),
  importing: z.boolean(),
})
export type AdContributorDto = z.infer<typeof adContributorDtoSchema>

/** What every view says about how current it is. */
export const adFreshnessDtoSchema = z.object({
  /** The last day shown: the latest day every updating account has. Null with no numbers yet. */
  cutoff: calendarDaySchema.nullable(),
  /** The oldest of the updating accounts' last updates. */
  lastUpdatedAt: instantSchema.nullable(),
  /** Some account behind the view is being read from Google right now. */
  importing: z.boolean(),
})

/** A venue's Ads page: its periods, newest first, split by track when it has tracks. */
export const venueAdsDtoSchema = adFreshnessDtoSchema.extend({
  venueId: z.string().uuid(),
  venueName: z.string(),
  /** The venue's tracks, in order; empty for a venue without them. */
  tracks: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  periods: z.array(
    z.object({
      window: adPeriodWindowDtoSchema,
      venue: adComparedDtoSchema,
      /** One per track, then null for the whole venue. Empty for a venue without tracks. */
      tracks: z.array(z.object({ trackId: z.string().uuid().nullable(), numbers: adComparedDtoSchema })),
    }),
  ),
  accounts: z.array(adContributorDtoSchema),
})
export type VenueAdsDto = z.infer<typeof venueAdsDtoSchema>

/**
 * A period's couples from Google Ads, campaign by campaign
 * (docs/ad-attribution.spec.md): who inquired, toured and booked from each, and
 * what they booked against what the campaign spent.
 */
export const adAttributionDtoSchema = z.object({
  view: leadStatsViewSchema,
  /** Each campaign with couples or spend, by spend, then the couples with no campaign known. */
  rows: z.array(sourceRowDtoSchema),
  /** Whether the venue records what its couples booked: through its proposals and contracts. */
  valuesRecorded: z.boolean(),
  /** Of the period's Google Ads inquiries, how many have no campaign known. */
  noCampaignCount: z.number().int().nonnegative(),
})
export type AdAttributionDto = z.infer<typeof adAttributionDtoSchema>

/** A venue's or the company's Google Ads bookings in a period, and what they booked against the spend. */
export const adBookingsDtoSchema = z.object({
  bookings: z.number().int().nonnegative(),
  /** US cents; null where nothing records what couples booked. */
  bookedValueCents: z.number().int().nonnegative().nullable(),
  /** Booked value per dollar spent; null with no spend, no value, or spend in another currency. */
  returnRatio: z.number().nonnegative().nullable(),
})
export type AdBookingsDto = z.infer<typeof adBookingsDtoSchema>

/** One period at a venue, campaign by campaign. */
export const venueAdsPeriodDtoSchema = z.object({
  window: adPeriodWindowDtoSchema,
  campaigns: z.array(
    z.object({
      campaignId: z.string().uuid(),
      name: z.string(),
      accountName: z.string(),
      /** The track it counts toward; null for the venue as a whole. */
      trackId: z.string().uuid().nullable(),
      numbers: adComparedDtoSchema,
    }),
  ),
  /** The period's couples from Google Ads, by campaign; null at a venue the pipeline can't be read for. */
  attribution: adAttributionDtoSchema.nullable().default(null),
})
export type VenueAdsPeriodDto = z.infer<typeof venueAdsPeriodDtoSchema>

/** The company's table: one row per venue that any numbers count toward, for one period. */
export const companyAdsDtoSchema = adFreshnessDtoSchema.extend({
  /** The period shown; null with no numbers yet. */
  window: adPeriodWindowDtoSchema.nullable(),
  /** Every period there are numbers for, newest first, to choose from. */
  periodStarts: z.array(calendarDaySchema),
  rows: z.array(
    z.object({
      venueId: z.string().uuid(),
      venueName: z.string(),
      numbers: adComparedDtoSchema,
      /** One of its accounts isn't updating: its numbers may be short. */
      notUpdating: z.boolean(),
      /** Its couples from Google Ads who inquired in the period and have booked. */
      bookings: adBookingsDtoSchema.nullable().default(null),
    }),
  ),
  totals: adComparedDtoSchema,
  /** The venues' Google Ads bookings added up. */
  bookings: adBookingsDtoSchema.nullable().default(null),
})
export type CompanyAdsDto = z.infer<typeof companyAdsDtoSchema>
